import { Core } from "@faire/design-tokens";
import { Row } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { observer } from "mobx-react";
import * as React from "react";
import styled, { css } from "styled-components";

interface ISidebarProps {
  desktopWidth?: string;
  xLargeWidth?: string;
  showStickySidebar?: boolean;
  noMaxWidth?: boolean;
}

interface IProps extends ISidebarProps {
  sidebar: React.ReactNode;
  content: React.ReactNode;
}

const SidebarAndContentBase: React.FC<IProps> = (props) => {
  const {
    desktopWidth,
    xLargeWidth,
    sidebar,
    content,
    showStickySidebar,
    noMaxWidth = false,
  } = props;

  return (
    <div>
      <Row
        style={
          noMaxWidth
            ? undefined
            : { width: "100%", margin: "auto", maxWidth: "1280px" }
        }
      >
        {sidebar ? (
          <SidebarContainer
            $showStickySidebar={showStickySidebar}
            $desktopWidth={desktopWidth}
            $xLargeWidth={xLargeWidth}
          >
            {sidebar}
          </SidebarContainer>
        ) : null}
        <ContentContainer>{content}</ContentContainer>
      </Row>
    </div>
  );
};

export const SidebarAndContent = observer(SidebarAndContentBase);

const SidebarContainer = styled.div<{
  $desktopWidth?: string;
  $xLargeWidth?: string;
  $showStickySidebar?: boolean;
}>`
  ${(props) =>
    props.$xLargeWidth
      ? css`
          ${responsiveMediaQueries.xLargeAndAbove} {
            width: ${props.$xLargeWidth};
          }
        `
      : null}

  ${(props) =>
    props.$desktopWidth
      ? css`
          ${responsiveMediaQueries.desktop} {
            width: ${props.$desktopWidth};
          }
        `
      : null}

  ${(props) =>
    props.$showStickySidebar
      ? css`
          ${responsiveMediaQueries.desktopAndAbove} {
            position: sticky;
            top: 128px;
            height: fit-content;
            overflow-x: hidden;
            overflow-y: auto;
            display: flex;
            flex-shrink: 0;
            &::-webkit-scrollbar {
              background-color: transparent;
              width: 8px;
            }
            &::-webkit-scrollbar-thumb {
              background-color: transparent;
            }
            &:hover&::-webkit-scrollbar-thumb {
              background-color: ${Core.surface
                .disabled}; // TODO replace with non-disabled surface token
            }
          }
          ${responsiveMediaQueries.tabletAndBelow} {
            display: none;
          }
        `
      : ""};

  ${responsiveMediaQueries.mobile} {
    position: fixed;
    width: 100%;
    height: 100%;
    z-index: 101;
    top: 0;
    display: none;
  }

  ${responsiveMediaQueries.tablet} {
    position: fixed;
    width: 320px;
    top: 0;
    left: -320px;
    z-index: 102;
    transition: 0.3s;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  min-width: 0;
`;
